import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { TRButton } from "@/components/TRButton";

const easing = [0.16, 1, 0.3, 1];
const AUTOPLAY_MS = 5000;
const A_URL =
    "https://customer-assets-agu9un31.emergentagent.net/job_brandship-hub/artifacts/";

// Curated slides. Each drives image + eyebrow + 2-line headline + subtitle + CTAs.
const SLIDES = [
    {
        key: "look-women-black",
        image: `${A_URL}fdgpjqse_image.png`,
        eyebrow: "Tony Rays — The Everyday System",
        lines: ["Simplicity", "is Freedom."],
        subtitle:
            "Twenty pairs. One simple choice. Your drawer, finally organized.",
        primary: { label: "Shop Women", to: "/shop/socks/women", testid: "hero-shop-women" },
        secondary: { label: "Shop Men", to: "/shop/socks/men", testid: "hero-shop-men" },
    },
    {
        key: "look-men-black",
        image: `${A_URL}w12jkrzx_image.png`,
        eyebrow: "Made for Him — Complete System",
        lines: ["One identity.", "Every day."],
        subtitle:
            "Socks, boxers, undershirts — one look that works every morning.",
        primary: { label: "Shop Men", to: "/shop/socks/men", testid: "hero-slide-men" },
        secondary: { label: "The Essential Box", to: "/shop/essentials/men", testid: "hero-slide-men-essentials" },
    },
    {
        key: "look-women-white",
        image: `${A_URL}ba9lro3d_image.png`,
        eyebrow: "Made for Her — Complete System",
        lines: ["Everything", "matches. Always."],
        subtitle:
            "The complete white system — from crew to socks, coordinated by design.",
        primary: { label: "Shop Women", to: "/shop/socks/women", testid: "hero-slide-women" },
        secondary: { label: "The Essential Box", to: "/shop/essentials/women", testid: "hero-slide-women-essentials" },
    },
    {
        key: "look-men-white",
        image: `${A_URL}nxhq4exx_image.png`,
        eyebrow: "Premium Boxers & Undershirts",
        lines: ["Same cut.", "Every single day."],
        subtitle:
            "Premium cotton, invisible under everything. Made to disappear — and last.",
        primary: { label: "Shop Boxers", to: "/shop/boxers/men", testid: "hero-slide-boxers" },
        secondary: { label: "Shop Undershirts", to: "/shop/undershirts/men", testid: "hero-slide-undershirts" },
    },
    {
        key: "look-women-set",
        image: `${A_URL}hp4vdi7n_image.png`,
        eyebrow: "Premium Socks — The One Box",
        lines: ["Twenty pairs.", "One decision."],
        subtitle:
            "Same model, same color — twenty times. Never think about socks again.",
        primary: { label: "Discover The One Box", to: "/shop/socks/women", testid: "hero-slide-socks" },
    },
];

// Masked line for the cinematic split-reveal on ENTER only.
// (Exit is handled globally by the wrapper for a clean, ghost-free fade.)
const Line = ({ text, delay = 0 }) => (
    <span className="mask-line-slider" aria-hidden="true">
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.05, delay, ease: easing }}
        >
            {text}
        </motion.span>
    </span>
);

const FadeText = ({ children, delay = 0, y = 20 }) => (
    <motion.div
        initial={{ opacity: 0, y }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay, ease: easing }}
    >
        {children}
    </motion.div>
);

export default function HeroSlider() {
    const [index, setIndex] = useState(0);
    const [direction, setDirection] = useState(1);
    const [paused, setPaused] = useState(false);
    const heroRef = useRef(null);
    const timerRef = useRef(null);

    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

    const total = SLIDES.length;
    const slide = SLIDES[index];

    const goTo = useCallback(
        (next) => {
            setDirection(next > index || (index === total - 1 && next === 0) ? 1 : -1);
            setIndex((next + total) % total);
        },
        [index, total]
    );
    const next = useCallback(() => goTo((index + 1) % total), [goTo, index, total]);
    const prev = useCallback(() => goTo((index - 1 + total) % total), [goTo, index, total]);

    // Autoplay with pause-on-hover
    useEffect(() => {
        if (paused) return;
        timerRef.current = setTimeout(() => {
            setDirection(1);
            setIndex((i) => (i + 1) % total);
        }, AUTOPLAY_MS);
        return () => clearTimeout(timerRef.current);
    }, [index, paused, total]);

    return (
        <section
            ref={heroRef}
            data-testid="hero-slider"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            className="relative min-h-[100svh] flex items-end overflow-hidden pt-32 md:pt-40"
        >
            {/* IMAGE LAYER — cross-fade + Ken Burns */}
            <motion.div
                style={{ y, scale }}
                className="absolute right-0 top-0 h-full w-full md:w-[58%] z-0"
            >
                <AnimatePresence mode="sync">
                    <motion.div
                        key={slide.key}
                        className="absolute inset-0"
                        initial={{ opacity: 0, scale: 1.06 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.02 }}
                        transition={{ duration: 1.4, ease: easing }}
                    >
                        <img
                            src={slide.image}
                            alt={slide.lines.join(" ")}
                            className="h-full w-full object-cover"
                            data-testid={`hero-image-${slide.key}`}
                            loading="eager"
                            fetchpriority="high"
                            decoding="async"
                        />
                    </motion.div>
                </AnimatePresence>
                <div className="absolute inset-0 bg-gradient-to-r from-paper via-paper/30 to-transparent md:from-paper/90 md:via-transparent" />
            </motion.div>

            {/* CONTENT LAYER — one clean fade for the whole block on transition */}
            <div className="relative z-10 max-w-[1500px] mx-auto w-full px-5 sm:px-8 pb-16 md:pb-24">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={slide.key}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.55, ease: easing }}
                    >
                        <FadeText delay={0.05}>
                            <span className="eyebrow text-ink/60" data-testid="hero-eyebrow">
                                {slide.eyebrow}
                            </span>
                        </FadeText>

                        <h1
                            className="mt-6 font-serif font-light tracking-[-0.04em] text-ink text-[15vw] leading-[0.9] sm:text-[12vw] md:text-[8.5vw] lg:text-[7.5rem]"
                            data-testid="hero-headline"
                        >
                            {slide.lines.map((line, i) => (
                                <Line
                                    key={`${slide.key}-${i}`}
                                    text={line}
                                    delay={0.15 + i * 0.11}
                                />
                            ))}
                        </h1>

                        <FadeText delay={0.5}>
                            <p
                                className="mt-8 max-w-md text-base md:text-lg text-ink/70 leading-relaxed"
                                data-testid="hero-subtitle"
                            >
                                {slide.subtitle}
                            </p>
                        </FadeText>

                        <FadeText delay={0.65}>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <TRButton
                                    as="link"
                                    to={slide.primary.to}
                                    data-testid={slide.primary.testid}
                                >
                                    {slide.primary.label}
                                    <ArrowRight size={15} strokeWidth={1.5} />
                                </TRButton>
                                {slide.secondary && (
                                    <TRButton
                                        as="link"
                                        to={slide.secondary.to}
                                        variant="outline"
                                        data-testid={slide.secondary.testid}
                                    >
                                        {slide.secondary.label}
                                    </TRButton>
                                )}
                            </div>
                        </FadeText>
                    </motion.div>
                </AnimatePresence>

                {/* CONTROLS */}
                <div className="mt-14 md:mt-20 flex items-center justify-between gap-6 max-w-[1500px]">
                    {/* Counter + progress line */}
                    <div className="flex items-center gap-5 text-ink/70">
                        <span
                            className="font-serif text-2xl tabular-nums text-ink"
                            data-testid="hero-counter-current"
                        >
                            {String(index + 1).padStart(2, "0")}
                        </span>
                        <div className="relative h-px w-24 sm:w-40 bg-ink/15 overflow-hidden">
                            <motion.div
                                key={`bar-${index}-${paused}`}
                                initial={{ scaleX: 0 }}
                                animate={{ scaleX: paused ? 0.001 : 1 }}
                                transition={{
                                    duration: paused ? 0 : AUTOPLAY_MS / 1000,
                                    ease: "linear",
                                }}
                                style={{ transformOrigin: "left center" }}
                                className="absolute inset-0 bg-ink"
                            />
                        </div>
                        <span
                            className="eyebrow text-ink/50 tabular-nums"
                            data-testid="hero-counter-total"
                        >
                            {String(total).padStart(2, "0")}
                        </span>
                    </div>

                    {/* Prev / Next + Dots */}
                    <div className="flex items-center gap-4 md:gap-6">
                        <div className="hidden sm:flex items-center gap-2">
                            {SLIDES.map((s, i) => (
                                <button
                                    key={s.key}
                                    type="button"
                                    onClick={() => goTo(i)}
                                    aria-label={`Go to slide ${i + 1}`}
                                    data-testid={`hero-dot-${i}`}
                                    className={`h-[6px] transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                                        i === index
                                            ? "w-8 bg-ink"
                                            : "w-2 bg-ink/25 hover:bg-ink/50"
                                    }`}
                                />
                            ))}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={prev}
                                aria-label="Previous slide"
                                data-testid="hero-prev"
                                className="h-11 w-11 border border-ink/25 flex items-center justify-center text-ink hover:bg-ink hover:text-paper transition-colors duration-500"
                            >
                                <ArrowLeft size={16} strokeWidth={1.5} />
                            </button>
                            <button
                                type="button"
                                onClick={next}
                                aria-label="Next slide"
                                data-testid="hero-next"
                                className="h-11 w-11 border border-ink/25 flex items-center justify-center text-ink hover:bg-ink hover:text-paper transition-colors duration-500"
                            >
                                <ArrowRight size={16} strokeWidth={1.5} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
